import { computed } from "vue";
import { fabric } from "fabric";
import { store } from "@/store";
import { drawLine } from "@/canvas-api/CreateObjects";
import useMountCanvas from "@/composables/useMountCanvas";
import { LineProperties } from "@/types";

const marksPerInch = 8;
const eigthInch = 5;
const quarterInch = 10;
const halfInch = 15;

const rulerTicks: LineProperties = {
  type: "line",
  id: "rulerTicks",
  name: "rulerTicks",
  originX: "center",
  originY: "center",
  stroke: "black",
  strokeWidth: 0.5,
  selectable: false,
  evented: false,
  strokeDashArray: undefined,
};

const labelWidth = computed<number>(() => store.getters.labelWidth);
const labelHeight = computed<number>(() => store.getters.labelHeight);
const dpi = computed<number>(() => store.getters.dimensions.units.dpi);

export const makeRulerCanvas = (
  canvas: fabric.Canvas,
  canvasWidth: number,
  canvasHeight: number,
  containerClass: string,
  rulerRef: HTMLCanvasElement
): fabric.Canvas => {
  canvas = useMountCanvas(canvas, rulerRef, canvasWidth, canvasHeight, "transparent");

  const canvasEl = document.querySelector(`#${rulerRef.getAttribute("id")}`) as HTMLCanvasElement;
  const canvasContainer = canvasEl.parentNode as HTMLDivElement;
  canvasContainer.classList.value = containerClass;

  return canvas;
};

export const drawLeftRuler = (
  center: number,
  canvasWidth: number,
  canvasHeight: number,
  baseLineProps: LineProperties,
  zoom: number
) => {
  const baseLineSize = canvasWidth - baseLineProps.strokeWidth;
  const baseLine = drawLine([baseLineSize, 0, baseLineSize, canvasHeight], baseLineProps);

  const ticks: (fabric.Line | fabric.Text)[] = [];

  const labelHeightZoomed = labelHeight.value * zoom;
  const start = center - labelHeightZoomed / 2;
  const ticksBottom = canvasHeight / 8;

  let startBottom = start;
  let startTop = start;

  let bottomTick;
  let topTick;
  let rulerNum = 0;
  
  for (let i = 0; i < ticksBottom; i++) {
    if (i % 4 === 0) {
      bottomTick = drawLine(
        [baseLineSize, startBottom, baseLineSize - halfInch, startBottom],
        rulerTicks
      );

      ticks.push(bottomTick);

      topTick = drawLine([baseLineSize, startTop, baseLineSize - halfInch, startTop], rulerTicks);

      ticks.push(topTick);
    } else if (i % 2 === 0) {
      bottomTick = drawLine(
        [baseLineSize, startBottom, baseLineSize - quarterInch, startBottom],
        rulerTicks
      );

      ticks.push(bottomTick);

      topTick = drawLine(
        [baseLineSize, startTop, baseLineSize - quarterInch, startTop],
        rulerTicks
      );

      ticks.push(topTick);
    } else {
      bottomTick = drawLine(
        [baseLineSize, startBottom, baseLineSize - eigthInch, startBottom],
        rulerTicks
      );

      ticks.push(bottomTick);

      topTick = drawLine([baseLineSize, startTop, baseLineSize - eigthInch, startTop], rulerTicks);

      ticks.push(topTick);
    }

    if (i % 8 === 0) {
      const bottomText = new fabric.Text(`${rulerNum}`, {
        fontSize: 10,
        fontFamily: "Open Sans",
      });

      bottomText.set({
        left: baseLineSize - halfInch - bottomText.height!,
        top: startBottom + bottomText.width! / 2,
        angle: 270,
        objectCaching: false,
      });

      const topText = new fabric.Text(`${rulerNum}`, {
        fontSize: 10,
        fontFamily: "Open Sans",
      });

      topText.set({
        left: baseLineSize - halfInch - bottomText.height!,
        top: startTop + bottomText.width! / 2,
        angle: 270,
        objectCaching: false,
      });

      ticks.push(bottomText);
      ticks.push(topText);

      rulerNum += 1;
    }

    startBottom += (dpi.value / marksPerInch) * zoom;
    startTop -= (dpi.value / marksPerInch) * zoom;
  }

  return new fabric.Group([baseLine, ...ticks], {
    selectable: false,
    evented: false,
    objectCaching: false,
  });
};

export const drawTopRuler = (
  center: number,
  canvasWidth: number,
  canvasHeight: number,
  baseLineProps: LineProperties,
  zoom: number
) => {
  const baseLineSize = canvasHeight - baseLineProps.strokeWidth;
  const baseLine = drawLine([0, baseLineSize, canvasWidth, baseLineSize], baseLineProps);

  const ticks: (fabric.Line | fabric.Text)[] = [];

  const labelWidthZoomed = labelWidth.value * zoom;
  const start = center - labelWidthZoomed / 2;
  const ticksRight = canvasWidth / 8;

  let startRight = start;
  let startLeft = start;

  let rightTick;
  let leftTick;
  let rulerNum = 0;

  for (let i = 0; i < ticksRight; i++) {
    if (i % 4 === 0) {
      rightTick = drawLine(
        [startRight, baseLineSize, startRight, baseLineSize - halfInch],
        rulerTicks
      );

      ticks.push(rightTick);

      leftTick = drawLine(
        [startLeft, baseLineSize, startLeft, baseLineSize - halfInch],
        rulerTicks
      );

      ticks.push(leftTick);
    } else if (i % 2 === 0) {
      rightTick = drawLine(
        [startRight, baseLineSize, startRight, baseLineSize - quarterInch],
        rulerTicks
      );

      ticks.push(rightTick);

      leftTick = drawLine(
        [startLeft, baseLineSize, startLeft, baseLineSize - quarterInch],
        rulerTicks
      );

      ticks.push(leftTick);
    } else {
      rightTick = drawLine(
        [startRight, baseLineSize, startRight, baseLineSize - eigthInch],
        rulerTicks
      );

      ticks.push(rightTick);

      leftTick = drawLine(
        [startLeft, baseLineSize, startLeft, baseLineSize - eigthInch],
        rulerTicks
      );

      ticks.push(leftTick);
    }

    if (i % 8 === 0) {
      const rightText = new fabric.Text(`${rulerNum}`, {
        fontSize: 10,
        fontFamily: "Open Sans",
      });

      rightText.set({
        left: startRight - rightText.width! / 2,
        top: baseLineSize - halfInch - rightText.height!,
        objectCaching: false,
      });

      const leftText = new fabric.Text(`${rulerNum}`, {
        fontSize: 10,
        fontFamily: "Open Sans",
      });

      leftText.set({
        left: startLeft - leftText.width! / 2,
        top: baseLineSize - halfInch - leftText.height!,
        objectCaching: false,
      });

      ticks.push(rightText);
      ticks.push(leftText);

      rulerNum += 1;
    }

    startRight += (dpi.value / marksPerInch) * zoom;
    startLeft -= (dpi.value / marksPerInch) * zoom;
  }

  return new fabric.Group([baseLine, ...ticks], {
    selectable: false,
    evented: false,
    objectCaching: false,
  });
};
